import type * as Bridge from "@shopfront/bridge";
import * as axios from "axios";
import moment from "moment/moment";
import type { Unsubscribe } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import { SALE_TRANSACTION_FINISHED } from "../../../_Bootstrap/GlobalConstants";
import { history, store } from "../../../_Bootstrap/RoutingAndStore";
import { registerAudioTrack } from "../../../Actions/Audio/AudioActions";
import { hideDialog, showDialog } from "../../../Actions/Dialogs/DialogActions";
import { startLoading, stopLoading } from "../../../Actions/General/LayoutActions";
import {
    actionModeToSummaryMode,
    changeSellScreenActionMode,
    changeSellScreenSummaryMode,
    generateInvoiceId,
    isSellScreenActionMode,
    isSellScreenSummaryMode,
    type SellScreenSummaryMode,
    updateTakings,
} from "../../../Actions/SellScreen/SellScreenActions";
import { OKOptionButton } from "../../../Common/Buttons/Button";
import PopupManager from "../../../Common/Popups/PopupManager";
import type BaseDataSource from "../../../Data/_Bases/BaseDataSource";
import type { LocalDatabaseSaleItem, LocalDatabaseSalePayment } from "../../../Data/_Bases/BaseSalesRepository";
import type { DataSourceTables } from "../../../Data/_Bases/DataSourceTables";
import { determineDataSource } from "../../../Data/DataStores/DataSource";
import AudioRequestDialog from "../../../Layout/Header/AudioMenu/AudioRequestDialog";
import type { SaleState, SaleStatus } from "../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import NotFoundException from "../../../Routes/SellScreen/Exceptions/Data/NotFoundException";
import ApplicationDownload from "../../../Routes/Settings/Integrations/ApplicationDownload";
import ApplicationRedirect from "../../../Routes/Settings/Integrations/ApplicationRedirect";
import { upload } from "../../../Workers/Upload/UploadAgent";
import { UPLOAD_TYPES } from "../../../Workers/Upload/UploadEvents";
import PromotionAgent from "../../Domain/Promotions/PromotionAgent";
import { scaleToDecimalPlaces } from "../../Generic/Numbers";
import UUID from "../../Generic/UUID";
import { printComponent } from "../../UI/PrintHelper";
import AudioManager, {
    AudioManagerEvents,
    type AudioTrack,
    AudioTrackEvents,
} from "../Audio/AudioManager";
import { DISABLE_EMBEDDED } from "../CurrentRequest";
import FulfilmentManager, {
    type FulfilmentOptions,
    type FulfilmentOrder,
    type FulfilmentOrderCreate,
} from "../Fulfilment/FulfilmentManager";
import { getLogLevel, LogLevel } from "../Logging";
import { getOption, type Options } from "../Options/Options";
import Request, { getVendorSubdomain } from "../Request";
import type { ApplicationCommunicator, ApplicationMessageHandlerData } from "./ApplicationCommunicator";
import { ApplicationDOMElement } from "./ApplicationDOMElement";
import {
    type ApplicationEventsFromRedux,
    ApplicationEventsFromShopfront,
    ApplicationEventsToShopfront,
    ApplicationInternalEvents,
} from "./ApplicationEvents";
import { ApplicationFrameCommunicator } from "./ApplicationFrameCommunicator";
import { ApplicationJavaScriptCommunicator } from "./ApplicationJavaScriptCommunicator";
import { getApplicationManager } from "./ApplicationUtilities";
import { EmbeddedSaleUpdate, type EmbeddedSaleUpdateData } from "./EmbeddedSaleUpdate";
import { UnsupportedApplicationEventError } from "./UnsupportedApplicationEventError";

interface DatabaseRequest {
    requestId: string;
    table: keyof DataSourceTables;
    method: string;
    args: Array<unknown>;
}

class DatabaseError extends Error {
    public publicReason: string;
    protected previousError?: Error;

    constructor(publicReason: string, internalReason?: string | Error) {
        super(typeof internalReason === "undefined" ? publicReason : internalReason.toString());
        this.publicReason = publicReason;

        if(internalReason instanceof Error) {
            this.previousError = internalReason;
        }
    }
}

interface CallbackData<ReturnData> {
    application: string;
    data: ReturnData | UnsupportedApplicationEventError;
}

export class ApplicationAgent {
    protected callbacks: Record<string, (data: CallbackData<any>) => void>;
    public id: string;
    protected name: string;
    protected key: string;
    protected expiryDate: Date;
    protected appGeneratedPublicKey: CryptoKey | undefined;
    protected importingKeyPromise: Promise<void> | undefined;
    protected connected: boolean;
    protected readyTimeout: NodeJS.Timeout | undefined;
    protected readyQueue: Array<() => void>;
    protected unsubscribeFromStore: Unsubscribe | false;
    protected currentLocation: false | string;
    protected applicationURL: string;
    protected audioRejected = false;
    protected audioTrack: AudioTrack | null = null;
    protected registerOutlet: {
        outlet: string | null;
        register: string | null;
        user: string | null;
    };
    protected externalMessageSources: Record<string, Window>;
    protected communicator!: ApplicationCommunicator;
    protected communicatorIsDestroyed: boolean;

    constructor(id: string, name: string, url: string, key: string, expiryDate: string) {
        if(process.env.NODE_ENV !== "test") {
            console.info(`Initializing application ${name}`);
        }

        this.id = id;
        this.name = name;
        this.key = key;
        this.connected = false;
        this.readyQueue = [];
        this.callbacks = {};
        this.unsubscribeFromStore = false;
        this.currentLocation = false;
        this.externalMessageSources = {};
        this.expiryDate = new Date(expiryDate);
        this.communicatorIsDestroyed = false;

        const state = store.getState();

        this.registerOutlet = {
            outlet  : state.location.outlet?.id || null,
            register: state.location.register?.id || null,
            user    : state.user.id || null,
        };

        this.handleAudioTrackRemoved = this.handleAudioTrackRemoved.bind(this);
        this.handleAudioReady = this.handleAudioReady.bind(this);

        this.applicationURL = url;

        this.create();
    }

    protected getName(): string {
        return this.name;
    }

    protected create(): void {
        // Determine which communicator to use
        if(this.applicationURL.endsWith(".js") || this.applicationURL.endsWith(".mjs")) {
            this.communicator = new ApplicationJavaScriptCommunicator(
                this.applicationURL,
                this.handleMessage,
                this.getName
            );
        } else {
            this.communicator = new ApplicationFrameCommunicator(
                this.applicationURL,
                this.handleMessage,
                this.getName
            );
        }

        // Build the communicator
        this.communicatorIsDestroyed = false;
        void this.communicator.create();

        // Listen for store changes
        this.unsubscribeFromStore = store.subscribe(this.handleStoreChange);

        // Listen for "external" internal page messages
        window.addEventListener("message", this.handleExternalMessage);

        // Listen for completed sales
        document.addEventListener(SALE_TRANSACTION_FINISHED, this.handleFinishedTransaction);

        // Will be cancelled if the application responds to the READY event
        this.readyTimeout = setTimeout(() => {
            if(process.env.NODE_ENV === "test" && this.connected) {
                // Occasionally this can trigger when using fake timers
                return;
            }

            console.error(`Did not receive ready response from application (${this.name})`);
            void this.communicator.destroy();

            const callbackIds = Object.keys(this.callbacks);

            // Trigger all associated callbacks
            for(const id of callbackIds) {
                this.handleCallback(id, new UnsupportedApplicationEventError(), "");
            }
        }, 3000);
    }

    protected updateKey(): void {
        this.sendMessage(ApplicationEventsFromShopfront.CYCLE_KEY, {
            key: this.key,
        });
    }

    public update(name: string, url: string, key: string, expiryDate: string): void {
        if(name !== this.name) {
            console.info(`Changed application name from ${this.name} to ${name}`);
            this.name = name;
        }

        if(this.applicationURL !== url) {
            console.info(`Changed ${this.name} URL`);
            this.applicationURL = url;
            this.key = key;

            const shouldUseJsCommunicator = url.endsWith(".js") || url.endsWith(".mjs");

            if(
                (
                    shouldUseJsCommunicator && this.communicator instanceof ApplicationFrameCommunicator
                ) || (
                    !shouldUseJsCommunicator && this.communicator instanceof ApplicationJavaScriptCommunicator
                )
            ) {
                // Change the communicator
                void this.communicator.destroy();

                if(shouldUseJsCommunicator) {
                    this.communicator = new ApplicationJavaScriptCommunicator(
                        this.applicationURL,
                        this.handleMessage,
                        this.getName
                    );
                } else {
                    this.communicator = new ApplicationFrameCommunicator(
                        this.applicationURL,
                        this.handleMessage,
                        this.getName
                    );
                }

                void this.communicator.create();
            } else {
                // Send the updated URL to the existing communicator
                void this.communicator.update(url);
            }
        } else if(key !== this.key) {
            this.key = key;
            this.expiryDate = new Date(expiryDate);
            this.updateKey();
        }
    }

    protected handleStoreChange = (): void => {
        const state = store.getState();

        const location = history.location;

        if(this.currentLocation !== location.pathname) {
            const oldLocation = this.currentLocation;

            this.currentLocation = location.pathname;
            this.send(ApplicationEventsFromShopfront.LOCATION_CHANGED, {
                from: oldLocation,
                to  : this.currentLocation,
            })
                .catch(e => {
                    if(e instanceof UnsupportedApplicationEventError) {
                        return;
                    }

                    throw e;
                });
        }

        const outlet = state.location.outlet?.id || null;
        const register = state.location.register?.id || null;
        const user = state.user.id || null;

        if(
            outlet !== this.registerOutlet.outlet ||
            register !== this.registerOutlet.register ||
            user !== this.registerOutlet.user
        ) {
            this.registerOutlet = {
                outlet,
                register,
                user,
            };

            this.send(ApplicationEventsFromShopfront.REGISTER_CHANGED, this.registerOutlet)
                .catch(e => {
                    if(e instanceof UnsupportedApplicationEventError) {
                        return;
                    }

                    throw e;
                });
        }
    };

    protected handleMessage = (data: ApplicationMessageHandlerData): void => {
        if(DISABLE_EMBEDDED) {
            return;
        }

        if(data.key !== this.id) {
            return;
        }

        // Typical cases
        if(typeof data.id !== "undefined" && typeof data.data !== "undefined") {
            if(this.handleCallback(data.id, data.data, data.type)) {
                return;
            }

            // If false it's probably not for us, but let it fall through to the special cases
        }

        // Listener events
        if(
            typeof ApplicationEventsToShopfront.LISTENERS[
                data.type as keyof typeof ApplicationEventsToShopfront.LISTENERS
            ] === "string"
        ) {
            if(
                data.type === ApplicationEventsToShopfront.LISTENERS.INTERNAL_PAGE_MESSAGE &&
                (data.data.method === "EXTERNAL_APPLICATION" || data.data.method === "PAYMENT_API")
            ) {
                this.handleExternalMessageReply(data.data);

                return;
            }

            getApplicationManager().emitEventFromApplication(data.type, this, data.data);

            return;
        }

        // Special cases
        switch(data.type) {
            case ApplicationEventsToShopfront.READY:
                clearTimeout(this.readyTimeout);
                this.connected = true;

                this.sendMessage(ApplicationEventsFromShopfront.READY, {
                    key     : this.key,
                    outlet  : this.registerOutlet.outlet,
                    register: this.registerOutlet.register,
                    vendor  : getVendorSubdomain(),
                });

                document.dispatchEvent(new CustomEvent(ApplicationInternalEvents.APPLICATION_READY, {
                    detail: {
                        id: this.id,
                    },
                }));

                if(AudioManager.getInstance().isReady()) {
                    this.handleAudioReady();
                } else {
                    AudioManager.getInstance().addListener(AudioManagerEvents.Ready, this.handleAudioReady);
                }

                if(this.readyQueue.length) {
                    for(let i = 0, l = this.readyQueue.length; i < l; i++) {
                        this.readyQueue[i]();
                    }

                    this.readyQueue = [];
                }

                break;
            case ApplicationEventsToShopfront.SERIALIZED:
                this.handleSerialized(data.data);
                break;
            case ApplicationEventsToShopfront.DOWNLOAD:
                this.handleDownload(data.data);
                break;
            case ApplicationEventsToShopfront.LOAD:
                this.handleLoad(data.data);
                break;
            case ApplicationEventsToShopfront.REQUEST_CURRENT_SALE:
                this.respondWithCurrentSale(data.data);
                break;
            case ApplicationEventsToShopfront.DATABASE_REQUEST:
                void this.handleDatabaseRequest(data.data);
                break;
            case ApplicationEventsToShopfront.REQUEST_LOCATION:
                this.handleLocationRequest(data.data);
                break;
            case ApplicationEventsToShopfront.PRINT_RECEIPT:
                this.handlePrintRequest(data.data);
                break;
            case ApplicationEventsToShopfront.CHANGE_SELL_SCREEN_ACTION_MODE:
                this.handleSellScreenActionRequest(data.data);
                break;
            case ApplicationEventsToShopfront.CHANGE_SELL_SCREEN_SUMMARY_MODE:
                this.handleSellScreenSummaryRequest(data.data);
                break;
            case ApplicationEventsToShopfront.REDIRECT:
                this.handleRedirect(data.data);
                break;
            case ApplicationEventsToShopfront.GET_OPTION:
                this.handleGetOption(data.data);
                break;
            case ApplicationEventsToShopfront.AUDIO_REQUEST_PERMISSION:
                this.handleRequestAudioPermission(data.data);
                break;
            case ApplicationEventsToShopfront.AUDIO_PRELOAD:
                void this.handlePreloadAudio(data.data);
                break;
            case ApplicationEventsToShopfront.AUDIO_PLAY:
                void this.handlePlayAudio(data.data);
                break;
            case ApplicationEventsToShopfront.REQUEST_SECURE_KEY:
                void this.handleSecureKeyRequest(data.data);
                break;
            case ApplicationEventsToShopfront.ROTATE_SIGNING_KEY:
                void this.handleSigningKeyRequest(data.data);
                break;
            case ApplicationEventsToShopfront.FULFILMENT_OPT_IN:
                void this.fulfilmentOptIn(data.data);
                break;
            case ApplicationEventsToShopfront.FULFILMENT_ORDERS_SYNC:
                void this.fulfilmentOrdersSync(data.data);
                break;
            case ApplicationEventsToShopfront.FULFILMENT_ORDERS_CREATE:
                void this.fulfilmentOrdersCreate(data.data);
                break;
            case ApplicationEventsToShopfront.FULFILMENT_ORDERS_UPDATE:
                void this.fulfilmentOrdersUpdate(data.data);
                break;
            case ApplicationEventsToShopfront.FULFILMENT_ORDERS_CANCEL:
                void this.fulfilmentOrdersCancel(data.data);
                break;
            case ApplicationEventsToShopfront.CREATE_SALE:
                void this.createSale(data.data);
                break;
        }
    };

    protected handleAudioReady = (): void => {
        this.sendMessage(ApplicationEventsFromShopfront.AUDIO_READY, {});
    };

    protected sendMessage = (type: string, data: unknown, id?: string): boolean => {
        if(this.communicatorIsDestroyed) {
            return false;
        }

        if(!this.isReady()) {
            this.readyQueue.push(() => {
                this.sendMessage(type, data, id);
            });

            return true;
        }

        return this.communicator.sendMessage(type, data, id);
    };

    public send<Return>(
        type: ApplicationEventsFromShopfront | ApplicationEventsFromRedux,
        data: unknown
    ): Promise<{
        application: string;
        data: Return;
    }> {
        // Perhaps look at implementing a ping system
        return new Promise((res, rej) => {
            const callbackId = this.createCallback<Return>((results) => {
                if(results.data instanceof UnsupportedApplicationEventError) {
                    rej(results.data);

                    return;
                }

                res(results as {
                    application: string;
                    data: Return;
                });
            });

            const sent = this.sendMessage(type, data, callbackId);

            if(!sent) {
                delete this.callbacks[callbackId];
                rej(new UnsupportedApplicationEventError());
            }
        });
    }

    protected createCallback<ReturnData>(
        callback: (data: CallbackData<ReturnData>) => void
    ): string {
        const id = UUID.generate();

        this.callbacks[id] = callback;

        return id;
    }

    protected handleCallback(id: string, data: Array<unknown> | unknown, type: string): boolean {
        if(typeof this.callbacks[id] === "undefined") {
            // Not for us
            return false;
        }

        // Convert the data if required
        let results;

        if(Array.isArray(data)) {
            results = [];

            for(let i = 0, l = data.length; i < l; i++) {
                if(typeof data[i] === "object" && data[i] !== null) {
                    if(typeof data[i].events === "object" && data[i].events !== null) {
                        const events: Record<string, (data: unknown) => void> = {};

                        for(const j in data[i].events) {
                            if(!Object.prototype.hasOwnProperty.call(data[i].events, j)) {
                                continue;
                            }

                            events[j] = ((events) => {
                                return (data: unknown): void => {
                                    for(let k = 0, n = events.length; k < n; k++) {
                                        this.send(ApplicationEventsFromShopfront.CALLBACK, {
                                            id: events[k],
                                            data,
                                        })
                                            .catch(e => {
                                                if(e instanceof UnsupportedApplicationEventError) {
                                                    return;
                                                }

                                                throw e;
                                            });
                                    }
                                };
                            })(data[i].events[j]);
                        }

                        results.push({
                            ...data[i],
                            events,
                        });

                        continue;
                    }
                }

                results.push(data[i]);
            }
        } else if(
            type === ApplicationEventsToShopfront.UNSUPPORTED_EVENT ||
            type === ApplicationEventsToShopfront.NOT_LISTENING_TO_EVENT
        ) {
            results = new UnsupportedApplicationEventError();
        } else {
            results = data;
        }

        this.callbacks[id]({
            application: this.id,
            data       : results,
        });

        delete this.callbacks[id];

        return true;
    }

    protected handleSerialized(data: {
        type: string;
        properties: Array<any>;
        events: Array<string>;
    }): void {
        switch(data.type) {
            case "Toast":
                switch(data.properties[0]) {
                    case "success":
                        PopupManager.success(data.properties[1]);
                        break;
                    case "error":
                        PopupManager.error(data.properties[1]);
                        break;
                    case "information":
                        PopupManager.info(data.properties[1]);
                        break;
                    case "warning":
                        PopupManager.warning(data.properties[1]);
                        break;
                    default:
                        console.warn("Unknown toast type sent from an embedded application", data);
                }
                break;
            case "InternalRedirect":
                history.push(data.properties[0]);
                break;
            case "ExternalRedirect":
                window.open(data.properties[0], "_blank");
                break;
            case "Dialog":
                (store.dispatch as ThunkDispatch<any, any, any>)(showDialog(
                    data.properties[2], // The header text
                    (
                        <div style={{ maxHeight: "70vh", overflow: "auto" }} className="from-integration">
                            {
                                <div
                                    className="integration-content"
                                    style={{ width: "fit-content", height: "fit-content" }}
                                >
                                    <ApplicationDOMElement
                                        id={`dialog-${this.id}-${UUID.generate()}`.toLowerCase()}
                                        content={data.properties[3]}
                                        application={this.id}
                                    />
                                </div>
                            }
                        </div>
                    ), // The content
                    data.properties[0], // The type of dialog
                    ((!data.properties[4] || data.properties[4].length === 0) ?
                        [ OKOptionButton ] :
                        data.properties[4].map((button: any) => ({
                            label   : button.properties[0],
                            classes : [ "button-cancel" ],
                            callback: (): void => {
                                const state = store.getState();
                                const user = state.user ? state.user.id : false;

                                if(button.events.click) {
                                    for(let i = 0, l = button.events.click.length; i < l; i++) {
                                        this.send(ApplicationEventsFromShopfront.CALLBACK, {
                                            id  : button.events.click[i],
                                            data: {
                                                from: "DIALOG",
                                                user,
                                            },
                                        })
                                            .catch(e => {
                                                if(e instanceof UnsupportedApplicationEventError) {
                                                    return;
                                                }

                                                throw e;
                                            });
                                    }
                                }
                            },
                        }))
                    ), // The buttons
                    false, // Focus
                    data.properties[1], // Whether the dialog is closable
                    data.properties[0] !== "frame" // Whether to show the dialog frame
                ));
                break;
            case "SaleUpdate":
                (new EmbeddedSaleUpdate(data as EmbeddedSaleUpdateData)).apply();
                break;
            default:
                console.warn("Unknown action sent to Shopfront from an embedded application", data);
        }
    }

    protected handleDownload(data: string): void {
        // noinspection SuspiciousTypeOfGuard
        if(typeof data !== "string") {
            return;
        }

        const downloadRule = getOption(`client_${this.id}_download`);

        if(downloadRule === false) {
            // Not allowed to download
            if(getLogLevel() >= LogLevel.NORMAL) {
                console.debug("Ignoring download request due to remembered rule");
            }
        } else if(downloadRule === true) {
            // Instantly download the file
            if(getLogLevel() >= LogLevel.NORMAL) {
                console.debug("Forcing download due to remembered rule");
            }

            window.open(data, "_blank");
        } else {
            // Ask for permission to download the file
            (store.dispatch as ThunkDispatch<any, any, any>)(showDialog(
                "Application Requests Download",
                <ApplicationDownload
                    id={this.id}
                    name={this.name}
                    file={data}
                />,
                "question",
                false
            ));
        }
    }

    protected handleRedirect(data: { to: string; external: boolean; }): void {
        // noinspection SuspiciousTypeOfGuard
        if(typeof data !== "object" || typeof data.to !== "string" || typeof data.external !== "boolean") {
            return;
        }

        if(!data.external) {
            // Always allow internal redirect
            history.push(data.to);

            return;
        }

        const redirectRule = getOption(`client_${this.id}_redirect`);

        if(redirectRule === false) {
            // Not allowed to redirect
            if(getLogLevel() >= LogLevel.NORMAL) {
                console.debug("Ignoring redirect request due to remembered rule");
            }
        } else if(redirectRule === true) {
            if(getLogLevel() >= LogLevel.NORMAL) {
                console.debug("Forcing redirect due to remembered rule");
            }

            window.location.href = data.to;
        } else {
            // Ask for permission to redirect the user
            (store.dispatch as ThunkDispatch<any, any, any>)(showDialog(
                "Application Requests External Redirect",
                <ApplicationRedirect
                    id={this.id}
                    name={this.name}
                    to={data.to}
                />,
                "question",
                false
            ));
        }
    }

    protected handleGetOption({ requestId, option }: { requestId: string; option: keyof Options; }): void {
        this.send(ApplicationEventsFromShopfront.RESPONSE_OPTION, {
            requestId,
            option,
            value: getOption(option),
        })
            .catch(e => {
                if(e instanceof UnsupportedApplicationEventError) {
                    return;
                }

                throw e;
            });
    }

    protected async sendAudioResponse(requestId: string, success: boolean, message?: string): Promise<void> {
        try {
            await this.send(ApplicationEventsFromShopfront.RESPONSE_AUDIO_REQUEST, {
                requestId,
                success,
                message,
            });
        } catch(e) {
            if(e instanceof UnsupportedApplicationEventError) {
                return;
            }

            throw e;
        }
    }

    protected handleAudioTrackRemoved(): void {
        if(!this.audioTrack) {
            return;
        }

        this.audioTrack.removeListener(AudioTrackEvents.removed, this.handleAudioTrackRemoved);
        this.audioTrack = null;

        this.send(ApplicationEventsFromShopfront.AUDIO_PERMISSION_CHANGE, {
            permitted: false,
        });
    }

    private createAudioTrack(): void {
        if(this.audioTrack !== null) {
            return;
        }

        this.audioRejected = false;
        this.audioTrack = AudioManager.getInstance().createTrack(this.id);
        this.audioTrack.addListener(AudioTrackEvents.removed, this.handleAudioTrackRemoved);

        (store.dispatch as ThunkDispatch<any, any, any>)(registerAudioTrack(
            this.name,
            this.audioTrack.id,
            this.id
        ));
    }

    protected handleRequestAudioPermission({ requestId }: { requestId: string; }): void {
        if(this.audioRejected) {
            this.sendAudioResponse(
                requestId,
                false,
                "Audio permission was rejected by user"
            );

            return;
        }

        if(this.audioTrack !== null) {
            this.sendAudioResponse(
                requestId,
                true
            );

            return;
        }

        let storedResponse = localStorage.getItem(`audio-permission-${this.id}`);

        if(storedResponse === null) {
            storedResponse = sessionStorage.getItem(`audio-permission-${this.id}`);
        }

        if(storedResponse !== null) {
            if(storedResponse === "true") {
                this.createAudioTrack();
                this.sendAudioResponse(
                    requestId,
                    true
                );

                return;
            } else if(storedResponse === "false") {
                this.audioRejected = true;
                this.sendAudioResponse(
                    requestId,
                    false,
                    "Audio permission was rejected by user"
                );

                (store.dispatch as ThunkDispatch<any, any, any>)(registerAudioTrack(
                    this.name,
                    null,
                    this.id
                ));

                return;
            }
        }

        if(!AudioManager.getInstance().isReady()) {
            this.sendAudioResponse(
                requestId,
                false,
                "Audio system isn't ready"
            );

            return;
        }

        (store.dispatch as ThunkDispatch<any, any, any>)(showDialog(
            "Request Audio",
            <AudioRequestDialog
                name={this.name}
                onComplete={(approved, remember) => {
                    (store.dispatch as ThunkDispatch<any, any, any>)(hideDialog());

                    // Store the results in memory
                    if(remember) {
                        localStorage.setItem(`audio-permission-${this.id}`, approved ? "true" : "false");
                    } else {
                        sessionStorage.setItem(`audio-permission-${this.id}`, approved ? "true" : "false");
                    }

                    if(approved) {
                        this.createAudioTrack();
                        this.sendAudioResponse(requestId, true);
                    } else {
                        this.audioRejected = true;
                        this.sendAudioResponse(
                            requestId,
                            false,
                            "Audio permission was rejected by user"
                        );

                        (store.dispatch as ThunkDispatch<any, any, any>)(registerAudioTrack(
                            this.name,
                            null,
                            this.id
                        ));
                    }
                }}
            />,
            "question"
        ));
    }

    protected async handlePreloadAudio({
        requestId,
        data,
    }: {
        requestId: string;
        data: { url: string; };
    }): Promise<void> {
        if(this.audioTrack === null || this.audioRejected) {
            return this.sendAudioResponse(
                requestId,
                false,
                "You do not have permission to load audio"
            );
        }

        try {
            await AudioManager.getInstance().loadSound(data.url, undefined, true);

            return this.sendAudioResponse(
                requestId,
                true
            );
        } catch(e) {
            console.error("Error whilst requesting audio preload", e);

            return this.sendAudioResponse(
                requestId,
                false,
                "An error occurred whilst trying to preload the audio"
            );
        }
    }

    protected async handlePlayAudio({
        requestId,
        data,
    }: {
        requestId: string;
        data: { url: string; };
    }): Promise<void> {
        if(this.audioTrack === null || this.audioRejected) {
            return this.sendAudioResponse(
                requestId,
                false,
                "You do not have permission to play audio"
            );
        }

        try {
            await AudioManager.getInstance().play(
                data.url,
                this.audioTrack,
                5 * 60 * 1000 // Five minutes in milliseconds
            );

            return this.sendAudioResponse(
                requestId,
                true
            );
        } catch(e) {
            console.error("Error whilst playing audio", e);

            return this.sendAudioResponse(
                requestId,
                false,
                "An error occurred whilst trying to play the audio"
            );
        }
    }

    protected handleLoad(start: boolean): void {
        // noinspection SuspiciousTypeOfGuard
        if(typeof start !== "boolean") {
            return;
        }

        if(start) {
            store.dispatch(startLoading());
        } else {
            store.dispatch(stopLoading());
        }
    }

    protected respondWithCurrentSale({ requestId }: { requestId: string; }): void {
        const state = store.getState();
        let sale: SaleState | false = state.sellscreen.sale;

        if(!state.location.register?.id) {
            sale = false;
        }

        this.send(ApplicationEventsFromShopfront.RESPONSE_CURRENT_SALE, {
            requestId,
            saleState: sale,
        })
            .catch(e => {
                if(e instanceof UnsupportedApplicationEventError) {
                    return;
                }

                throw e;
            });
    }

    protected async handleDatabaseRequest(request: DatabaseRequest): Promise<void> {
        try {
            const source = await determineDataSource();

            if(typeof source[request.table] === "undefined") {
                // noinspection ExceptionCaughtLocallyJS
                throw new DatabaseError("Table not found");
            }

            if(typeof (source[request.table] as any)[request.method] === "undefined") {
                // noinspection ExceptionCaughtLocallyJS
                throw new DatabaseError("Method does not exist on table");
            }

            let results;

            try {
                results = await (source[request.table] as any)[request.method](...request.args);
            } catch(e) {
                if(e instanceof NotFoundException) {
                    // noinspection ExceptionCaughtLocallyJS
                    throw new DatabaseError("Results not found", e);
                }

                // noinspection ExceptionCaughtLocallyJS
                throw new DatabaseError(
                    "Invalid request provided",
                    e instanceof Error || typeof e === "string" ?
                        e :
                        JSON.stringify(e)
                );
            }

            this.sendMessage(ApplicationEventsFromShopfront.RESPONSE_DATABASE_REQUEST, {
                requestId: request.requestId,
                results,
            });
        } catch(e) {
            console.error("Received database error when handling request from Embedded API", e, request);

            let reason = "Unknown database error";

            if(e instanceof DatabaseError) {
                reason = e.publicReason;
            }

            this.sendMessage(ApplicationEventsFromShopfront.RESPONSE_DATABASE_REQUEST, {
                requestId: request.requestId,
                error    : reason,
            });
        }
    }

    protected handleLocationRequest({ requestId }: { requestId: string; }): void {
        const state = store.getState();

        const outlet = state.location.outlet?.id || null;
        const register = state.location.register?.id || null;
        const user = state.user.id || null;

        this.send(ApplicationEventsFromShopfront.RESPONSE_LOCATION, {
            requestId,
            register,
            outlet,
            user,
        })
            .catch(e => {
                if(e instanceof UnsupportedApplicationEventError) {
                    return;
                }

                throw e;
            })
            .then(() => {
                this.handleStoreChange();
            });
    }

    protected handlePrintRequest({ content, type }: { content: string; type: "text"; }): void {
        if(type !== "text") {
            console.warn(`Received print request for type "${type}" and contains content:`, content);

            return;
        }

        printComponent(
            <pre>{content}</pre>
        );
    }

    protected handleSellScreenActionRequest({ mode }: { mode: string; }): void {
        if(!isSellScreenActionMode(mode)) {
            console.warn(`Received a sell screen action request for invalid mode "${mode}", ignoring.`);

            return;
        }

        (store.dispatch as ThunkDispatch<any, any, any>)(changeSellScreenActionMode(mode));

        // Check if we need to change the Summary mode as well
        if(mode in actionModeToSummaryMode) {
            (store.dispatch as ThunkDispatch<any, any, any>)(
                changeSellScreenSummaryMode(actionModeToSummaryMode[mode] as SellScreenSummaryMode)
            );
        }
    }

    protected handleSellScreenSummaryRequest({ mode }: { mode: string; }): void {
        if(!isSellScreenSummaryMode(mode)) {
            console.warn(`Received a sell screen summary request for invalid mode "${mode}", ignoring.`);

            return;
        }

        (store.dispatch as ThunkDispatch<any, any, any>)(changeSellScreenSummaryMode(mode));
    }

    protected handleExternalMessage = (event: MessageEvent): void => {
        // This method is designed to forward messages to our frame,
        // not to receive messages from our frame (handleMessage does that)
        if(!this.communicator) {
            return;
        }

        if(this.communicator.isEventFromSource(event)) {
            return;
        }

        if(typeof event.data !== "object" || event.data === null) {
            return;
        }

        if(event.data.type !== "INTERNAL_PAGE_MESSAGE") {
            return;
        }

        if(event.data.clientId !== this.id) {
            return;
        }

        if(typeof event.data.from !== "string") {
            return;
        }

        if(!event.data.externalApplication) {
            return;
        }

        if(typeof event.data.message !== "object" || event.data.message === null) {
            return;
        }

        if(event.source) {
            this.externalMessageSources[event.data.from] = event.source as Window;
        }

        // Valid message, forward on to our frame
        this.sendMessage(ApplicationEventsFromShopfront.INTERNAL_PAGE_MESSAGE, {
            method : "EXTERNAL_APPLICATION",
            url    : event.data.from,
            message: event.data.message,
        });
    };

    protected handleExternalMessageReply(data: {
        url: string;
        method: "EXTERNAL_APPLICATION" | "PAYMENT_API";
        message: unknown;
    }): void {
        if(typeof this.externalMessageSources[data.url] === "undefined") {
            return;
        }

        const url = this.communicator.getRawUrl();

        this.externalMessageSources[data.url].postMessage(
            {
                from   : "ShopfrontApplicationAgent",
                origins: {
                    to  : data.url,
                    from: url instanceof URL ? url.host : url,
                },
                type: ApplicationEventsFromShopfront.INTERNAL_PAGE_MESSAGE,
                data: data.message,
                id  : this.id,
            },
            "*"
        );
    }

    protected handleFinishedTransaction = (event: CustomEvent<false | SaleState>): void => {
        if(event.detail === false) {
            return;
        }

        this.sendMessage(ApplicationEventsFromShopfront.SALE_COMPLETE, {
            sale: {
                ...event.detail,
                createdAt: moment.isMoment(event.detail.createdAt) ?
                    event.detail.createdAt.toISOString(true) :
                    event.detail.createdAt,
            },
        });
    };

    protected handleSecureKeyRequest = async ({ requestId }: { requestId: string; }): Promise<void> => {
        if(typeof this.importingKeyPromise !== "undefined") {
            await this.importingKeyPromise;
        }

        if(typeof this.appGeneratedPublicKey === "undefined") {
            console.error("Key request has occurred before a public key has been generated");

            return;
        }

        if(this.expiryDate <= new Date()) {
            // Need to grab a new key
            try {
                const response = await Request.post("/embedded/generate", {
                    client_id: this.id,
                });

                this.key = response.data.token;
                this.expiryDate = new Date(response.data.expiryDate);
            } catch(e) {
                console.log("An error occurred while retrieving a new token", e);

                if(e instanceof axios.AxiosError) {
                    // User logged out
                    if(e.response?.status === 401) {
                        // Send an error response back to the application
                        this.sendMessage(ApplicationEventsFromShopfront.RESPONSE_SECURE_KEY, {
                            requestId,
                            data: {
                                error: true,
                                type : "USER_LOGGED_OUT",
                            },
                        });

                        return;
                    }
                }
            }
        }

        let data;
        let signature;

        try {
            data = new TextEncoder().encode(JSON.stringify({
                auth: this.key,
                id  : crypto.randomUUID(),
                app : this.id,
                user: this.registerOutlet.user,
                url : {
                    raw   : this.communicator.getRawUrl(),
                    loaded: this.communicator.getLoadedUrl(),
                },
            }));

            signature = await crypto.subtle.sign(
                {
                    name: "ECDSA",
                    hash: {
                        name: "SHA-384",
                    },
                },
                this.appGeneratedPublicKey,
                data
            );
        } catch(e) {
            console.error("Received error while encrypting a key request", e);

            return;
        }

        this.sendMessage(
            ApplicationEventsFromShopfront.RESPONSE_SECURE_KEY,
            {
                requestId,
                data,
                signature,
            }
        );
    };

    protected handleSigningKeyRequest = (data: JsonWebKey): void => {
        this.importingKeyPromise = new Promise<void>(res => {
            crypto.subtle.importKey("jwk", data, {
                name      : "ECDSA",
                namedCurve: "P-384",
            }, true, [ "sign" ])
                .then(key => {
                    this.appGeneratedPublicKey = key;
                    this.importingKeyPromise = undefined;
                    res();
                });
        });
    };

    protected fulfilmentOptIn(data: { options?: FulfilmentOptions; }): void {
        FulfilmentManager.getInstance().init(this.id, data.options);
    }

    protected fulfilmentOrdersSync(data: { orders: Array<FulfilmentOrderCreate>; merge: boolean; }): void {
        FulfilmentManager.getInstance().syncOrders(this.id, data.orders, data.merge);
    }

    protected fulfilmentOrdersCreate(data: { order: FulfilmentOrderCreate; }): void {
        FulfilmentManager.getInstance().createOrder(this.id, data.order);
    }

    protected fulfilmentOrdersUpdate(data: { order: FulfilmentOrder; }): void {
        FulfilmentManager.getInstance().updateOrder(this.id, data.order);
    }

    protected fulfilmentOrdersCancel(data: { id: string; }): void {
        FulfilmentManager.getInstance().deleteOrder(this.id, data.id);
    }

    protected async createSale(data: { requestId: string; sale: Bridge.SaleCreateData; }): Promise<void> {
        let register: undefined | string | null = data.sale.register;

        if(typeof register === "undefined") {
            register = this.registerOutlet.register;
        }

        if(register === null) {
            this.sendMessage(ApplicationEventsFromShopfront.RESPONSE_CREATE_SALE, {
                requestId: data.requestId,
                success  : false,
                message  : "Sale has not provided a register and Shopfront currently doesn't have a register selected.",
            });

            return;
        }

        const { user } = this.registerOutlet;

        if(user === null) {
            this.sendMessage(ApplicationEventsFromShopfront.RESPONSE_CREATE_SALE, {
                requestId: data.requestId,
                success  : false,
                message  : "A sale cannot be created when there is no user logged in to Shopfront.",
            });

            return;
        }

        const source = await determineDataSource();

        // Parse the created at string
        const createdAt: string = moment().toISOString(true);

        const id = UUID.generate();

        let totalPrice = 0;
        let totalQuantity = 0;
        let totalTax = 0;
        const items: Array<LocalDatabaseSaleItem> = [];

        for(let i = 0, l = data.sale.products.length; i < l; i++) {
            const product = data.sale.products[i];

            totalPrice += product.price;
            totalQuantity += product.caseQuantity * product.quantity;

            const item = await formatCreateSaleProduct(source, product);

            totalTax += item.tax;

            items.push(item);
        }

        let paid = 0;
        const takings: Record<string, number> = {};
        const payments: Array<LocalDatabaseSalePayment> = [];

        for(let i = 0, l = data.sale.payments.length; i < l; i++) {
            const payment = data.sale.payments[i];

            const method = await source.paymentMethods.get(payment.method);

            if(payment.cashout !== 0) {
                this.sendMessage(ApplicationEventsFromShopfront.RESPONSE_CREATE_SALE, {
                    requestId: data.requestId,
                    success  : false,
                    message  : "Sale payment with cash out is currently unsupported " +
                        "through the Embedded Fulfilment API.",
                });

                return;
            }

            if(payment.rounding !== 0) {
                this.sendMessage(ApplicationEventsFromShopfront.RESPONSE_CREATE_SALE, {
                    requestId: data.requestId,
                    success  : false,
                    message  : "Sale payment with rounding is currently unsupported " +
                        "through the Embedded Fulfilment API.",
                });

                return;
            }

            payments.push({
                amount         : payment.amount,
                metadata       : "",
                cashout        : payment.cashout,
                rounding       : payment.rounding,
                paymentMethodId: method.uuid,
                registerId     : register,
                status         : payment.status || "completed",
                processTime    : createdAt,
                receipt        : null,
                subtype        : null,
                cancelledAt    : payment.status === "cancelled" ? createdAt : null,
            });

            paid += payment.amount;

            if(typeof takings[payment.method] !== "number") {
                takings[payment.method] = 0;
            }

            takings[payment.method] += payment.amount;
        }

        // Calculate remaining (Round to nearest 4 decimal places)
        const remaining = scaleToDecimalPlaces(totalPrice - paid, 4);

        // Check to see if the sale has been over-paid
        if(remaining < 0) {
            this.sendMessage(ApplicationEventsFromShopfront.RESPONSE_CREATE_SALE, {
                requestId: data.requestId,
                success  : false,
                message  : "Total paid is greater than sale total.",
            });

            return;
        }

        let customer = null;

        if(data.sale.customer) {
            customer = await source.customers.get(data.sale.customer);
        }

        // Validate if the sale has been under-paid
        let status: SaleStatus = "COMPLETED";

        if(remaining > 0) {
            if(!customer) {
                this.sendMessage(ApplicationEventsFromShopfront.RESPONSE_CREATE_SALE, {
                    requestId: data.requestId,
                    success  : false,
                    message  : "Total paid is less than sale total.",
                });

                return;
            }

            if(!customer.allow_account_sales) {
                this.sendMessage(ApplicationEventsFromShopfront.RESPONSE_CREATE_SALE, {
                    requestId: data.requestId,
                    success  : false,
                    message  : "The provided customer does not allow account / incomplete sales.",
                });

                return;
            }

            if(typeof customer.account_limit === "number") {
                if((customer.current_owing + remaining) > customer.account_limit) {
                    this.sendMessage(ApplicationEventsFromShopfront.RESPONSE_CREATE_SALE, {
                        requestId: data.requestId,
                        success  : false,
                        message  : "The provided customer does not have enough remaining account " +
                            "balance to complete this sale without payment.",
                    });

                    return;
                }
            }

            status = "INCOMPLETE";
        }

        const state = store.getState();
        const offline = !state.information.online;

        await source.sales.put({
            id,
            items,
            status,
            totalTax,
            payments,
            createdAt,
            linkedTo  : null,
            cashout   : 0,
            clientId  : this.id,
            customerId: customer?.uuid ?? null,
            discount  : {
                reason: "",
                amount: "0",
            },
            notes         : data.sale.notes,
            orderReference: data.sale.orderReference,
            refundReason  : "",
            invoiceId     : generateInvoiceId(offline),
            registerId    : register,
            loyaltyValue  : 0,
            totalBase     : 0,
            totalChange   : 0,
            totalCost     : 0,
            totalPrice    : Math.round(totalPrice * 100) / 100,
            totalQuantity : Math.round(totalQuantity),
            totalRounding : 0,
            updatedAt     : createdAt,
            userId        : user,
            uploaded      : false,
            actions       : [],
            metaData      : data.sale.metaData,
        });

        const takingsToUpdate = [];

        for(const i in takings) {
            if(!Object.prototype.hasOwnProperty.call(takings, i)) continue;

            takingsToUpdate.push({
                method: i,
                amount: takings[i],
            });
        }

        await updateTakings(takingsToUpdate);

        await upload(UPLOAD_TYPES.SALE, id);

        this.sendMessage(ApplicationEventsFromShopfront.RESPONSE_CREATE_SALE, {
            requestId: data.requestId,
            success  : true,
        });
    }

    public isReady(): boolean {
        return this.connected && this.communicator.isReady();
    }

    public destroy(): void {
        // Mark as not connected
        this.connected = false;
        this.communicatorIsDestroyed = true;

        // Stop the communicator
        void this.communicator.destroy();

        // Stop listening to store changes
        if(this.unsubscribeFromStore) {
            this.unsubscribeFromStore();
            this.unsubscribeFromStore = false;
        }

        // Stop listening for "external" internal page messages
        window.removeEventListener("message", this.handleExternalMessage);

        // Stop listening for finished transactions
        document.removeEventListener(SALE_TRANSACTION_FINISHED, this.handleFinishedTransaction);
    }
}

async function formatCreateSaleProduct(
    source: BaseDataSource,
    product: Bridge.SaleCreateProductData
): Promise<LocalDatabaseSaleItem> {
    const item = await source.products.get(product.uuid);

    let taxRate = item.tax_rate ? parseFloat(item.tax_rate.amount) : 0;

    if(isNaN(taxRate)) {
        taxRate = 0;
    }

    const tax = product.price - (product.price / (1 + (taxRate / 100)));
    const everydayPrice = await PromotionAgent.everydayPrice(product.uuid, product.quantity);

    return {
        tax,
        basePrice   : Math.abs(everydayPrice / product.quantity),
        caseQuantity: product.caseQuantity,
        discount    : {
            amount: "0",
            reason: "",
        },
        item: {
            id  : item.uuid,
            type: "Product",
            name: item.name,
        },
        loyaltyValue: 0,
        note        : product.note,
        products    : await Promise.all(
            product.products.map(p => {
                return formatCreateSaleProduct(source, p);
            })
        ),
        promotions: [],
        quantity  : product.quantity,
        savings   : 0,
        taxRate   : item.tax_rate?.uuid ?? null,
        totalPrice: product.price,
        metaData  : product.metaData,
        priceList : false,
    };
}
